import React, { useEffect, createContext, useState } from 'react';
import { Button, Popover, Badge, Flex, Card, Typography } from 'antd';
import Icon from '@mdi/react';
import { mdiFilter } from '@mdi/js';
import WebsitesSelect from 'components/Selects/WebsitesSelect';
import { useFormik } from 'formik';
import AdSelect from 'components/Selects/AdSelect';
import AdsetNameSelect from 'components/Selects/AdsetNameSelect';
import CampaignSelect from 'components/Selects/CampaignSelect';
import { useWidgetDate } from 'store/redux/states/dates/dates.hooks';
import DateRangePicker from 'components/DateRangePicker';
import useQueryParams from 'hooks/useQueryParams';
import useWorkspace from 'store/redux/hooks/useWorkspace';

export const JourneyContext = createContext({});

const JourneyReport = (props) => {
  const { workspace } = useWorkspace();
  const selected_website_id = localStorage.getItem(`selected_website_id_${workspace.id}`);

  const {
    applyGlobally = false,
    title,
    date_key,
    header,
    shape,
    disableDate = false,
    showfilter = true,
    headStyle,
  } = props;
  const { date_from, date_to, date_range_key } = useWidgetDate(date_key);
  const { searchParams, setUrlParams, removeParams } = useQueryParams();
  const { adset_ids, ad_ids, campaign_ids, website_id = selected_website_id } = searchParams;

  const parseIds = (value) => (value?.length ? value.split(',').map(Number) : []);

  const cleanFilters = (values) =>
    Object.fromEntries(
      Object.entries(values).filter(([, v]) => v != null && !(Array.isArray(v) && v.length === 0))
    );

  const [filter, setFilter] = useState(() =>
    cleanFilters({
      website_id: website_id || [],
      ad_ids: parseIds(ad_ids),
      adset_ids: parseIds(adset_ids),
      campaign_ids: parseIds(campaign_ids),
      date_from,
      date_to,
      date_range_key,
    })
  );

  const [count, setCount] = useState();

  const [open, setOpen] = useState(false);

  useEffect(() => {
    const values = {
      website_id: website_id || [],
      ad_ids: parseIds(ad_ids),
      adset_ids: parseIds(adset_ids),
      campaign_ids: parseIds(campaign_ids),
      date_from,
      date_to,
      date_range_key,
    };

    form.setValues(values);

    const filters = cleanFilters(values);
    getFilterCount(filters);

    if (!applyGlobally) {
      setFilter(filters);
    }
  }, [website_id, date_from, ad_ids, adset_ids, campaign_ids]);

  function handleFilter() {
    const filtersToSet = cleanFilters(form.values);

    if (applyGlobally) {
      setUrlParams(filtersToSet);
    } else {
      setFilter(filtersToSet);
      getFilterCount(filtersToSet);
    }

    setOpen(false);
  }

  const handleClear = () => {
    const cleared = cleanFilters({
      website_id: form.values.website_id,
      date_from,
      date_to,
      date_range_key,
    });

    if (applyGlobally) {
      removeParams(['ad_ids', 'adset_ids', 'campaign_ids']);
    } else {
      setFilter(cleared);
      getFilterCount(cleared);
    }

    form.setValues({
      website_id: form.values.website_id,
      ad_ids: [],
      adset_ids: [],
      campaign_ids: [],
    });
    setCount(0);
    setOpen(false);
  };

  const getFilterCount = (values) => {
    let count = 0;
    if (website_id) count += 0;
    if (values?.ad_ids && values.ad_ids.length) count += 1;
    if (values?.adset_ids && values.adset_ids.length) count += 1;
    if (values?.campaign_ids && values.campaign_ids.length) count += 1;
    setCount(count);
  };

  const form = useFormik({
    initialValues: {
      website_id: website_id || [],
      ad_ids: ad_ids?.length ? ad_ids.split(',').map(Number) : [],
      adset_ids: adset_ids?.length ? adset_ids.split(',').map(Number) : [],
      campaign_ids: campaign_ids?.length ? campaign_ids.split(',').map(Number) : [],
    },
    onSubmit: handleFilter,
  });

  const onValueChange = (name, value) => {
    form.setFieldValue(name, value);

    if (applyGlobally) {
      if (!value || (Array.isArray(value) && value.length === 0)) {
        removeParams([name]);
      }
    }
  };

  return (
    <JourneyContext.Provider value={{ filter, setFilter }}>
      <Card
        title={
          title && (
            <Typography className='text-sm w-full overflow-hidden text-ellipsis text-left font-bold'>
              {title}
            </Typography>
          )
        }
        headStyle={headStyle}
        size='medium'
        bodyStyle={{ padding: 0 }}
        extra={
          <Flex justify='space-between' align='center' wrap='wrap' gap={10} className='w-full'>
            {header}
            <Flex gap={5}>
              {!disableDate && (
                <DateRangePicker
                  variant='outlined'
                  placement='bottomRight'
                  className='h-9'
                  style={{ maxWidth: 150 }}
                  formatter='yyyy-MM-dd'
                  changeGlobal={true}
                  placeholder={['Date from', 'Date to']}
                  date_key={date_key}
                  globalDate={false}
                  value={[date_from, date_to]}
                  icon={false}
                  date_range_key={date_range_key}
                />
              )}
              {applyGlobally && (
                <WebsitesSelect
                  className='w-full h-9 round-select'
                  allowClear={false}
                  value={Number([form?.values?.website_id]) || []}
                  onChange={(val) => {
                    localStorage.setItem(`selected_website_id_${workspace.id}`, val);
                    form.setFieldValue('website_id', val);
                    setUrlParams({ website_id: val });
                  }}
                />
              )}
              {showfilter && (
                <Popover
                  className='flex items-center gap-1'
                  content={
                    <form className='w-full sm:w-96 '>
                      <div className='grid w-full h-full grid-cols-1 gap-2 '>
                        <AdSelect
                          multiple
                          value={form.values.ad_ids}
                          onChange={(val) => onValueChange('ad_ids', val)}
                        />
                        <AdsetNameSelect
                          multiple
                          value={form.values.adset_ids}
                          onChange={(val) => onValueChange('adset_ids', val)}
                        />
                        <CampaignSelect
                          className='w-full'
                          multiple
                          value={form.values.campaign_ids}
                          onChange={(val) => onValueChange('campaign_ids', val)}
                        />
                      </div>
                      <div className='flex items-center justify-between col-span-12 gap-2 mt-4'>
                        <div className='flex items-center justify-between w-full gap-4 mr-2'>
                          <Button
                            icon={<img src='/icons/reset-orange.svg' className='w-3 h-3' />}
                            onClick={handleClear}
                          >
                            Reset
                          </Button>

                          <Button type='primary' onClick={handleFilter}>
                            Filter
                          </Button>
                        </div>
                      </div>
                    </form>
                  }
                  trigger='click'
                  open={open}
                  onOpenChange={setOpen}
                >
                  <Badge className='h-9' count={count} size='small' offset={[-5, 4]}>
                    <Button
                      shape={shape}
                      icon={<Icon path={mdiFilter} size={0.7} />}
                      className='h-full'
                    >
                      Filter
                    </Button>
                  </Badge>
                </Popover>
              )}
            </Flex>
          </Flex>
        }
      >
        {props.children}
      </Card>
    </JourneyContext.Provider>
  );
};

export default JourneyReport;
